// src/components/Controls/Popups/PopupInv.js

import React, { forwardRef, memo, useCallback, useRef, useState } from 'react';
import Draggable from 'react-draggable';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  LinearProgress,
  Paper,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';

import { setPopupInv } from '../../../redux/slices/popupSlice';
import { setFormTouchedInv } from '../../../redux/slices/formTouchedSlice';
import ButtonAction from '../Buttons/ButtonAction';
import ConfirmTouched from './ConfirmTouched';

const assignRef = (ref, value) => {
  if (typeof ref === 'function') {
    ref(value);
    return;
  }

  if (ref) {
    ref.current = value;
  }
};

const DraggablePaper = forwardRef(function DraggablePaper(props, forwardedRef) {
  const nodeRef = useRef(null);

  const handleRef = useCallback(
    (node) => {
      nodeRef.current = node;

      assignRef(forwardedRef, node);
    },
    [forwardedRef]
  );

  return (
    <Draggable nodeRef={nodeRef} handle=".popupinv-drag-handle" cancel=".popupinv-cancel">
      <Paper {...props} ref={handleRef} />
    </Draggable>
  );
});

const PopupInv = memo(({ title, children }) => {
  const { t } = useTranslation();

  const dispatch = useDispatch();

  const theme = useTheme();

  const lgMediaDown = useMediaQuery(theme.breakpoints.down('lg'));

  const loadingStatus = useSelector((state) => state.loadingStatusSlice?.loadingStatus ?? false);

  const formTouchedInv = useSelector((state) => state.formTouchedSlice?.formTouchedInv ?? false);

  const popupInv = useSelector((state) => state.popupSlice?.popupInv ?? false);

  const invCompRef = useRef(null);

  const [confirmTouched, setConfirmTouched] = useState({
    isOpen: false,
    title: '',
    subTitle: '',
    type: '',
  });

  const closeConfirmation = useCallback(() => {
    setConfirmTouched((current) => ({
      ...current,
      isOpen: false,
    }));
  }, []);

  const confirmEscClose = useCallback(() => {
    dispatch(setPopupInv(false));
  }, [dispatch]);

  const handleEscClose = useCallback(() => {
    if (!formTouchedInv) {
      confirmEscClose();
      return;
    }

    setConfirmTouched({
      isOpen: true,

      title: t('popup.you_have_unsaved_changes'),

      subTitle: '',

      type: '',

      onBackToPage: closeConfirmation,

      onSave: async () => {
        try {
          await invCompRef.current?.handleSubmit?.();

          dispatch(setFormTouchedInv(false));

          closeConfirmation();
        } catch (error) {
          console.error('Invoice save failed:', error);
        }
      },

      onConfirm: () => {
        dispatch(setFormTouchedInv(false));

        closeConfirmation();
        confirmEscClose();
      },
    });
  }, [closeConfirmation, confirmEscClose, dispatch, formTouchedInv, t]);

  const childrenWithProps = React.Children.map(children, (child) => {
    if (React.isValidElement(child)) {
      return React.cloneElement(child, {
        ref: invCompRef,
      });
    }

    return child;
  });

  return (
    <>
      <Dialog
        open={popupInv}
        maxWidth="xl"
        fullWidth={false}
        disableEnforceFocus
        onClose={handleEscClose}
        PaperComponent={DraggablePaper}
        PaperProps={{
          sx: {
            mt: 2,

            m: lgMediaDown ? 0.75 : undefined,
          },
        }}
        BackdropProps={{
          style: {
            backgroundColor: 'rgba(0,0,0,0.1)',
          },
        }}
        sx={{
          '& .MuiDialog-container': {
            justifyContent: 'center',

            alignItems: 'flex-start',
          },
        }}
      >
        <DialogTitle
          sx={{
            bgcolor: '#bdd600',
          }}
        >
          <Grid container alignItems="center">
            <Grid
              item
              xs={10}
              className="popupinv-drag-handle"
              sx={{
                cursor: 'move',
              }}
            >
              <Typography variant="h6">{title}</Typography>
            </Grid>

            <Grid container item xs={2} justifyContent="flex-end" className="popupinv-cancel">
              <ButtonAction type="button" color="secondary" onClick={handleEscClose}>
                {t('button.esc')}
              </ButtonAction>
            </Grid>
          </Grid>
        </DialogTitle>

        <Box
          sx={{
            width: '100%',
          }}
        >
          {loadingStatus ? <LinearProgress /> : <LinearProgress variant="determinate" value={100} />}
        </Box>

        <DialogContent>{childrenWithProps}</DialogContent>
      </Dialog>

      <ConfirmTouched confirmTouched={confirmTouched} setConfirmTouched={setConfirmTouched} />
    </>
  );
});

DraggablePaper.displayName = 'DraggablePaper';

PopupInv.displayName = 'PopupInv';

export default PopupInv;
